import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import withStyles from '@mui/styles/withStyles';
import {Avatar, Backdrop, Button, Dialog, DialogActions, DialogTitle, DialogContent, DialogContentText, Divider, Grid, Link, Paper, TextField, Tooltip, Typography} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CancelCircleOutline from '@mui/icons-material/Cancel';
import Email from '@mui/icons-material/Email';
import Lock from '@mui/icons-material/Lock';
import VerifiedUser from '@mui/icons-material/VerifiedUser';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import Airplay from '@mui/icons-material/Airplay';
import OndemandVideo from '@mui/icons-material/OndemandVideo';
import AccountBalance from '@mui/icons-material/AccountBalance';

import AbEnvironment from '../auth/AbEnvironment';
import AccountActivationFetch from './AccountActivationFetch';
import AccountActivationUpdateMutation from './AccountActivationUpdateMutation';
import AccountUpdateMutation from '../account/AccountUpdateMutation';
import UserUpdateMutation from '../account/UserUpdateMutation';
import ServerError from '../ui/ServerError';
import SavingSpinner from '../ui/SavingSpinner';
import LoadingSpinner from '../ui/LoadingSpinner';

import { Client } from 'persona';

import { red, green, amber } from '@mui/material/colors';

const abEnvironment = new AbEnvironment();
const AccountActivationConfigurableStepsQuery = graphql`
query AccountActivationConfigurableStepsQuery($accountId: UUID!) {
  onboardingAccountTByAccountId(accountId: $accountId) {
    onboardingId
    onboardingAid
    onboardingName
    onboardingDesc
    onboardingText
    onboardingTitle
    onboardingStatus
    onboardingResult
    onboardingAccountStepTsByOnboardingAid(orderBy: STEP_ORDER_ASC) {
      totalCount
      nodes {
        onboardingAstepId
        stepOrder
        stepName
        stepDesc
        stepIcon
        stepText
        stepTitle
        stepAction
        stepTarget
        stepStatus
        stepResult
        stepButtonIcon
        stepButtonText
      }
    }
  }
  getappintegrationsforaccount(accountid: $accountId) {
    totalCount
    nodes {
      appIntegrationId
      appName
      appDescription
      appLogo
      active
      accountDelegatedAuthsId
      appData
    }
  }
}
`;

const styleSheet = theme => ({
    paper: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      // marginTop: 32,
      // paddingTop: 16,
      // paddingLeft: 10,
      // paddingRight: 10,
      // paddingBottom: 20,
      color: theme.palette.text.secondary,
    },
    inset : {
      paddingLeft: 24,
      paddingRight: 24,
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: '#fff'
    },
    icon: {
      marginRight: '24px',
      height: '2em',
      width: '2em'
    },
    continue: {
      marginBottom: '16px',
    },
    stepsdiv: {
      width: '90%'
    }
});

class AccountActivationSteps extends Component {
    
  constructor(props) {
    super(props);

    this.state = {
      backdropOpen: true,
      personaLoading: false,
      templateId: null,
      environment: null,

      nameUpdateOpen: false,
      updateNameSpinner: false,
      connectToFreshBooks: false,
      uLastName: abEnvironment.getUserAttribute("a_lastName"),
      uFirstName: abEnvironment.getUserAttribute("a_firstName"),
      fetchCount: 0,

      mountComplete: false
    }
  }

  /* Get persona information, and check FreshBooks connect status if applicable */
  componentDidMount() {
    const { callPartnerApi, logError } = this.props.auth;
    let aPjwt = abEnvironment.getPjwt();

    let payload = {
      operation: "getPersonaTemplateInfo",
      params: {
        pJwt: aPjwt
      }
    };

    let theTemplateId;
    let theEnvironment;

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      theTemplateId = response.templateId;
      theEnvironment = response.environment;

      // Check if there is a current active step for connecting FreshBooks, and if FreshBooks
      // is connected.  If so, we need to mark the step as completed.
      //
      return AccountActivationFetch.fetch(abEnvironment.getUserAttribute('a_id')).toPromise();
    })
    .then(aaf => {
      // let onboardingStatus = aaf.onboardingAccountTByAccountId.onboardingStatus;
      let onboardingId = aaf.onboardingAccountTByAccountId.onboardingId;
      let onboardingAstepId = null;
      let stepStatus = 0;
      if (aaf.onboardingAccountTByAccountId.onboardingAccountStepTsByOnboardingAid.totalCount > 0) {
        onboardingAstepId = aaf.onboardingAccountTByAccountId.onboardingAccountStepTsByOnboardingAid.nodes[0].onboardingAstepId;
        stepStatus = aaf.onboardingAccountTByAccountId.onboardingAccountStepTsByOnboardingAid.nodes[0].stepStatus;
      }

      if (onboardingAstepId != null) {
        // Depending on status, may need to update something
        // 0: Pending, 1: Ready, 2: In process, 3: Complete, 4: Complete with errors
        // The only state change we'll do is that if the status < 3 and fbConnected is true,
        // change the status to 3
        let fbConnected = false;
        if (aaf.onboardingAccountTByAccountId.accountTByAccountId.accountDelegatedAuthsTsByAccountId.totalCount > 0) {
          fbConnected = true;
        }
  
        if (stepStatus < 3 && fbConnected === true) {
          //mark the step completed
          payload = {
            operation: "completeAccountActivationStep",
            params: {
              pJwt: aPjwt,
              pAccountId: abEnvironment.getUserAttribute('a_id'),
              onboardingId: onboardingId,
              onboardingAstepId: onboardingAstepId
            }
          };
          
          return callPartnerApi('appbind/generic',
                        'POST',
                        payload
          );
        }
      }
      return null;
    })
    .then(resp => {
      this.setState({templateId: theTemplateId, environment: theEnvironment, mountComplete: true});
    })
    .catch(error => {
      logError('Exception in AccountActivationSteps.js: ' + error);
      if (theTemplateId != null && theEnvironment != null) {
        this.setState({templateId: theTemplateId, environment: theEnvironment, mountComplete: true});
      } else {
        this.setState({mountComplete: true});
      }
    });    
  }

  openPersona() {

    this.setState({personaLoading: true, updateNameSpinner: false});
    let email = abEnvironment.getUserAttribute("a_email");
    let firstName = abEnvironment.getUserAttribute("a_firstName");
    let lastName = abEnvironment.getUserAttribute("a_lastName");

    const client = new Client({
      templateId: this.state.templateId,
      environment: this.state.environment,
      fields: {
        nameFirst: firstName,
        nameLast: lastName,
        emailAddress: email
      },
      onLoad: (error) => {
        if (error) {
          console.error(`Failed with code: ${error.code} and message ${error.message}`)
        }
        client.open();
        this.setState({personaLoading: false});
      },
      onCancel: (inquiry) => {
        //console.log(`Cancelling, updating account with inquiry id`);
        this.updateAccountWithInquiryId(inquiry.inquiryId);
      },
      onComplete: (inquiry) => {
        //console.log(`Complete, calling backend`);
        this.updateAccountWithStatus(inquiry.inquiryId);
      }
    });

  }

  updateUserAndGo() {
    this.setState({ updateNameSpinner: true });
    const { uLastName, uFirstName } = this.state; 

    let aid = abEnvironment.getUserAttribute('a_id');
    let uid = abEnvironment.getUserAttribute('u_id');
    UserUpdateMutation.commit(uid,
                              uLastName, uFirstName, null, null,
                              null, null, null, null, null, null,
                              null)
    .then(response => {
      // Call an update mutation to set on the account
      return AccountUpdateMutation.commit(aid, null, null, null, null,
                                          uLastName, uFirstName, null, null,
                                          null, null, null, null, null, null,
                                          null, null, null, null, null,
                                          null, null, null);

      }).then(response => {
        abEnvironment.setUserAttribute('a_lastName', uLastName);
        abEnvironment.setUserAttribute('a_firstName', uFirstName);
        abEnvironment.setUserAttribute('u_lastName', uLastName);
        abEnvironment.setUserAttribute('u_firstName', uFirstName);

        // console.log('User Update');
        this.setState({ nameUpdateOpen: false });
        this.openPersona();
    })
    .catch(error => {
      // console.log('Got error: ' + error);
      this.setState({ updateNameSpinner: false });
    });
  }



  updateAccountWithInquiryId(inquiryId) {
 
    const { logError } = this.props.auth;

    let accountId = abEnvironment.getUserAttribute("a_id");

    AccountActivationUpdateMutation.commit(accountId, inquiryId)
    .then(response => {
      //console.log('update good: ' + inquiryId);
    })
    .catch(error => {
      logError('Exception in AccountActivationSteps.js: ' + error);
    });

  }

  updateAccountWithStatus(inquiryId) {

    const { logError } = this.props.auth;

    this.setState({personaLoading: true});

    let accountId = abEnvironment.getUserAttribute("a_id");
    let accountName = abEnvironment.getUserAttribute("a_accountName");
    let status = '';

    const { callPartnerApi } = this.props.auth;

    //fetch environment info and present window.
    let aPjwt = abEnvironment.getPjwt();

    let payload = {
      operation: "getPersonaInquiryStatus",
      params: {
        pJwt: aPjwt,
        accountId: accountId,
        inqId: inquiryId
      }
    };

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {


      //mark the step completed or complete with error

      status = response.status;
      let step = this.state.verificationStep;
      let onboardingId = this.state.onboardingId
      let withError = false;
      if (status !== 'passed') {
        withError = true;
      }
      
      let payload = {
        operation: "completeAccountActivationStep",
        params: {
          pJwt: aPjwt,
          pAccountId: accountId,
          onboardingId: onboardingId,
          onboardingAstepId: step.onboardingAstepId,
          withError: withError
        }
      };
  
      
      return callPartnerApi('appbind/generic',
                     'POST',
                     payload
      )
    })
    .then(resp => {
      return resp.json();
    })
    .then(response => {


      //console.log(response);
      //console.log(JSON.stringify(response));

      let theBody = accountName + ' completed verification process. Status is ' + status;


      let payload = {
        operation: "createNotification",
        params: {
          pJwt:             aPjwt,
          accountId:        accountId,
          notificationType: 'verification',
          severity:         'info',
          subject:          'Verification check',
          body:             theBody
        }
      }
      
      return callPartnerApi('appbind/generic',
                     'POST',
                     payload
      )
    })
    .then(response => {

      this.setState({personaLoading: false, verificationStep: null, onboardingId: null});

      //temporary code
      if (localStorage.getItem("verificationAuto") === "1") {
        if (status === 'passed') {
          abEnvironment.setUserAttribute('a_aStatus', 0);
        }
      }

      if (status === 'passed') {
        abEnvironment.setUserAttribute('a_personaStatus', 'passed');
      }

 
      window.location.reload();

    })
    .catch(error => {
      logError('Exception in AccountActivationSteps.js: ' + error);
    });    


    //window.location.reload();

  }


  updateAccountWithVerificationComplete() {
    const { logError } = this.props.auth;

    let accountId = abEnvironment.getUserAttribute("a_id");

    AccountActivationUpdateMutation.commit(accountId, null, 1)
    .then(response => {
      window.location.reload();
    })
    .catch(error => {
      logError('Exception in AccountActivationSteps.js: ' + error);

    });

  }

  /*goToPersona() {
    
    this.props.history.push("/persona");
  }*/

  handleClose()  {
    localStorage.setItem("activateSteps", true);
    this.setState({backdropOpen: false});
  }


  openNameUpdateDialog(props, step) {
    //console.log(props.onboardingAccountTByAccountId.onboardingId);
    //console.log(step);
    this.setState({nameUpdateOpen: true, verificationStep: step, onboardingId: props.onboardingAccountTByAccountId.onboardingId});
  }


  openNameUpdateDialogComplete() {
    this.updateUserAndGo();
  }

  handleGenericStepClick(props, step) {

    //here we update the step to complete and then launch 
    const { logError } = this.props.auth;

    let fetchCount = this.state.fetchCount;
    fetchCount++;
    let accountId = abEnvironment.getUserAttribute("a_id");
    let onboardingId = props.onboardingAccountTByAccountId.onboardingId;
    let onboardingAstepId = step.onboardingAstepId;

    const { callPartnerApi } = this.props.auth;

    //fetch environment info and present window.
    let aPjwt = abEnvironment.getPjwt();

    let payload = {
      operation: "completeAccountActivationStep",
      params: {
        pJwt: aPjwt,
        pAccountId: accountId,
        onboardingId: onboardingId,
        onboardingAstepId: onboardingAstepId
      }
    };

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {

      //console.log(JSON.stringify(response));
      this.setState({fetchCount: fetchCount});
      window.open(step.stepTarget);
 
    })
    .catch(error => {
      logError('Exception in AccountActivationSteps.js: ' + error);
    });    

  }

  handleNameUpdateDialogClose() {
    this.setState({nameUpdateOpen: false});
  }

  activateClick() {
    localStorage.removeItem("activateSteps");
    this.setState({backdropOpen: true})
  }


  connectToFreshBooks(props, app, step) {
    // Want a redirect here, so use history replace.
    // Get the redirect uri from our internal API
    let fetchCount = this.state.fetchCount;
    fetchCount++;

    const { callPartnerApi } = this.props.auth;

    //fetch environment info and present window.
    this.setState({connectToFreshBooks: true});
    let connectData = null;

    // Set local storage to indicate that the next call to freshbooks settings is from onboarding
    localStorage.setItem("int_onboarding", "fb");

    let payload = {
            operation: "OAuth2PartnerRedirect",
            params: {
              appIntegrationId:  app.appIntegrationId
            }
        };

    Promise.resolve()
    .then(() => {
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      connectData = data;
      this.setState({connectToFreshBooks: false, fetchCount: fetchCount});
      //console.log(JSON.stringify(connectData.link));
      if (connectData.link) {
        window.location.replace(connectData.link);
      } else {
        window.location.reload();
      }
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  render() {
      const { logError } = this.props.auth;
      const classes = this.props.classes;
      const okColor = green[300];
      const failedColor = red[300];
      const statusWaiting = amber[400];
      const preventDefault = (event) => event.preventDefault();

      let accountId = abEnvironment.getUserAttribute("a_id");

      if (!this.state.mountComplete) {
        return (
          <LoadingSpinner />
        );
      }

      return (
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={AccountActivationConfigurableStepsQuery}
          variables={{accountId: accountId, fetchCount: this.state.fetchCount}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              //pending reseller

              if ((this.props.accountType === 3) && (this.props.accountStatus === 1)) {
                //suspended reseller
                return (
                  <Paper className={classes.paper} elevation={1} >
                    <div >
                      <Typography variant="h6">
                        Suspended account
                      </Typography>
                      <Typography variant="body2">
                        Your account has been suspended. You can review past activity, but cannot add customers, add funding sources, or purchase subscriptions through AppBind. Please contact support to learn how to recover your account.
                      </Typography>
          
                    </div>
          
                  </Paper>
                );  
              } 

              //find the freshbooks app in integrations case it is needed
              let freshBooksApp = null;
              let i = 0;
              for (i = 0; i < props.getappintegrationsforaccount.nodes.length; i++) {
                let app = props.getappintegrationsforaccount.nodes[i];
                if (app.appName === "FreshBooks") {
                  freshBooksApp = app;
                  //console.log(freshBooksApp);
                  break;
                }
              }

              let clickedOff = localStorage.getItem("activateSteps");

              let steps = (
                <Paper className={classes.paper} elevation={1} >
                  <Grid container spacing={2} direction="row" justifyContent="space-between">
                    {this.props.accountStatus === 2 &&
                    <Grid item xs={12}>
                      <Grid container spacing={0} direction="row" justifyContent="space-between" alignItems="center" className={classes.inset}>
                        <Grid item>
                          <Typography variant="h6">
                            {props.onboardingAccountTByAccountId.onboardingTitle}
                          </Typography>
                          <Typography variant="body2">
                            {props.onboardingAccountTByAccountId.onboardingText}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Grid container spacing={1} direction="row">
                            {(this.props.accountStatus === 2 || this.props.accountStatus === "2") &&
                            <React.Fragment>
                              <Grid item>
                                <HourglassEmpty htmlColor={statusWaiting}/>
                              </Grid>
                              <Grid item>
                                <Typography variant="body1">PENDING</Typography>
                              </Grid>
                            </React.Fragment>}
                            {(this.props.accountStatus === 0 || this.props.accountStatus === "0") &&
                            <React.Fragment>
                              <Grid item>
                                <CheckCircle htmlColor={okColor}/>
                              </Grid>
                              <Grid item>
                                <Typography variant="body1">ACTIVE</Typography>
                              </Grid>
                            </React.Fragment>}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    }
        
                    {props.onboardingAccountTByAccountId.onboardingAccountStepTsByOnboardingAid.nodes.map(step => {
                      return(

                        <React.Fragment key={step.onboardingAstepId}>

                          {/* email step */}
                          {step.stepAction === 0 &&
                          <React.Fragment>
                            <Grid item xs={12}><Divider/></Grid>
                            <Grid item>
                              <Grid container spacing={0} direction="row" alignItems="center">
                                <Grid item className={classes.inset}>
                                  <Avatar>
                                    <Email/>
                                  </Avatar>
                                </Grid>
                                <Grid item >
                                  <Typography variant="body1"><strong>{step.stepTitle}</strong></Typography>
                                  <Typography variant="body2">{step.stepText}</Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item>
                              <CheckCircle htmlColor={okColor} className={classes.icon} />
                            </Grid>
                          </React.Fragment>}

                          {/* persona verification step */}
                          {step.stepAction === 1 &&
                          <React.Fragment>
                            <Grid item xs={12}><Divider/></Grid>
                            <Grid item >
                              <Grid container spacing={0} direction="row">
                                <Grid item  className={classes.inset}>
                                  <Avatar>
                                    <VerifiedUser />
                                  </Avatar>
                                </Grid>
                                <Grid item >
                                  <Typography variant="body1"><strong>{step.stepTitle}</strong></Typography>
                                  <Typography variant="body2">{step.stepText}</Typography>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item alignItems="center">
                              {(step.stepStatus === 0 || step.stepStatus === 1) &&
                              <React.Fragment>
                                {this.state.personaLoading === false && step.stepButtonText === 'Verify' &&
                                <Button color="primary" variant={"contained"} disabled={step.stepStatus !== 1} style={{marginRight: '24px'}}
                                    onClick={() => { this.openNameUpdateDialog(props, step); }}>
                                        {step.stepButtonText}&nbsp;<Lock/>
                                </Button>}
                                {this.state.personaLoading === false && step.stepButtonText !== 'Verify' &&
                                <Button color="primary" variant={"contained"} disabled={step.stepStatus !== 1} style={{marginRight: '24px'}}
                                    onClick={() => { this.openNameUpdateDialog(props, step); }}>
                                        {step.stepButtonText}
                                </Button>}

                                {this.state.personaLoading === true &&
                                <Button disabled={true} color="primary" variant="outlined"  style={{marginRight: '24px'}} >
                                  <SavingSpinner/>
                                </Button>}
                                
                              </React.Fragment>}

                              {step.stepStatus === 3 &&
                              <CheckCircle htmlColor={okColor} className={classes.icon}/>}

                              {step.stepStatus === 4 &&
                              <Tooltip title='Your documents submitted failed verification.  Contact AppBind.'>
                                <CancelCircleOutline htmlColor={failedColor}  className={classes.icon}/>
                              </Tooltip>}
                            </Grid>
        
                          </React.Fragment>}

                          {/* freshbooks connect step */}
                          {step.stepAction === 2 &&
                          <React.Fragment>
                            <Grid item xs={12}><Divider/></Grid>
                            <Grid item >
                              <Grid container spacing={0} direction="row" alignItems="center">
                                <Grid item  className={classes.inset}>
                                  <Avatar>
                                    <AccountBalance />
                                  </Avatar>
                                </Grid>
                                <Grid item >
                                  <Typography variant="body1"><strong>{step.stepTitle}</strong></Typography>
                                  <Typography variant="body2">{step.stepText}</Typography>
                                </Grid>
                              </Grid>
                            </Grid>

                            <Grid item>
                              {(step.stepStatus === 0 || step.stepStatus === 1) &&
                              <React.Fragment>
                                {this.state.connectToFreshBooks === false &&
                                <Button color="primary" variant={"contained"} disabled={step.stepStatus !== 1} style={{marginRight: '24px'}}
                                    onClick={() => { this.connectToFreshBooks(props, freshBooksApp, step); }}>
                                        {step.stepButtonText}
                                </Button> 
                                }
                                {this.state.connectToFreshBooks === true &&

                                <Button disabled={true} color="primary" variant="outlined"  style={{marginRight: '24px'}} >
                                  <SavingSpinner/>
                                </Button> 
                                }
                              </React.Fragment>}

                              {step.stepStatus === 3 &&
                              <CheckCircle htmlColor={okColor} className={classes.icon}/>}
                            </Grid>
        
                          </React.Fragment>}

                          {/* other kind of step */}
                          {(step.stepAction !== 0) && (step.stepAction !== 1) && (step.stepAction !== 2) &&
                          <React.Fragment>
                            <Grid item xs={12}><Divider/></Grid>
                            <Grid item>
                              <Grid container spacing={0} direction="row" alignItems="center">
                                <Grid item className={classes.inset}>
                                  <Avatar>
                                    {step.stepIcon === "Airplay" &&
                                    <Airplay/>}
                                    {step.stepIcon === "OndemandVideo" &&
                                    <OndemandVideo/>}
                                  </Avatar>
                                </Grid>
                                <Grid item>
                                  <Typography variant="body1"><strong>{step.stepTitle}</strong></Typography>
                                  <Typography variant="body2">{step.stepText}</Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item>
                              {(step.stepStatus === 0 || step.stepStatus === 1) &&
                              <React.Fragment>
                                <Button color="primary" variant={"contained"} disabled={step.stepStatus !== 1} style={{marginRight: '24px'}}
                                    onClick={() => { this.handleGenericStepClick(props, step); }}>
                                        {step.stepButtonText}
                                </Button> 
                              </React.Fragment>}

                              {step.stepStatus === 3 &&
                              <React.Fragment>
                                <CheckCircle htmlColor={okColor} className={classes.icon}/>
                              </React.Fragment>}
                            </Grid>
                          </React.Fragment>}

                        </React.Fragment>
                    
                      );
                    })}
                        
                    {((this.props.accountStatus === 2) && (this.props.personaStatus === 'passed') && props.onboardingAccountTByAccountId.onboardingStatus === 2) &&
                    <React.Fragment>
                      <Grid item xs={12}><Divider/></Grid>
                      <Grid item xs={12} className={classes.continue}>
                        <Grid container spacing={0} direction="row">
                          <Grid item style={{minWidth: 106}}>
                            
                          </Grid>
                          <Grid item >
                            <Typography variant="body1"><strong>Thank you for verifying.</strong></Typography>
                            <Typography variant="body2">AppBind will review and activate your account.</Typography>
                          </Grid>
                        </Grid>
                          
                      </Grid>
                    </React.Fragment>}

                    {!clickedOff && this.state.backdropOpen && this.props.accountStatus === 2 && this.props.personaStatus !== 'passed' &&
                    <React.Fragment>
                      <Grid item xs={12}><Divider/></Grid>

                      <Grid item xs={12} className={classes.continue}>
                        <Grid container spacing={0} direction="row">
                          <Grid item style={{minWidth: 106}}>
                            
                          </Grid>
                          <Grid item >
                          <Typography variant="body1"><strong>Want to explore first?</strong> 
                            <Link href="#" onClick={preventDefault} style={{marginLeft: '8px', color: 'blue'}}>
                            Skip for now.
                            </Link> 
                          </Typography>
                          </Grid>
                        </Grid>
 
                      </Grid>
                    </React.Fragment>}
                    
                    <Dialog open={this.state.nameUpdateOpen} onClose={() => this.handleNameUpdateDialogClose()}>
                      <DialogTitle>Confirm the exact name on your ID</DialogTitle>
                      <DialogContent>
                        <DialogContentText>
                          We verify your identity using your government ID using an automated, online, secure system to preserve your privacy. Confirm this name <strong>exactly matches</strong> the name on the government ID you’ll use to verify your identity.
                        </DialogContentText>
                          <Grid container spacing={2} justifyContent="space-between" style={{marginTop: '16px'}}>
                            <Grid item xs={12} sm={6}>
                              <TextField autoFocus data-recording-ignore="mask"
                                                id="ufirst-name" label="First name"
                                                value={ this.state.uFirstName }
                                                onChange={ (e) => this.setState({ uFirstName: e.target.value }) }
                                                inputProps={{ maxLength: 32 }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField data-recording-ignore="mask"
                                                id="ulast-name" label="Last name"
                                                value={ this.state.uLastName }
                                                onChange={ (e) => this.setState({ uLastName: e.target.value }) }
                                                inputProps={{ maxLength: 32 }} />
                            </Grid>

                          </Grid>
                      </DialogContent>
                      <DialogActions>
                        <Button color="primary" variant={"outlined"} onClick={() => this.handleNameUpdateDialogClose()}>Cancel</Button>
                        {!this.state.updateNameSpinner &&
                          <Button color="primary" variant={"contained"}  onClick={() => this.openNameUpdateDialogComplete()}>Proceed with verification</Button>
                        }
                        {this.state.updateNameSpinner &&
                          <Button disabled={true} color="primary" variant="outlined" >
                            <SavingSpinner/>
                          </Button> 
                        }
                      </DialogActions>
                    </Dialog>
                  </Grid>
                </Paper>
              );

              
              if ((this.props.accountType === 3) && (this.props.accountStatus === 2 || this.props.accountStatus === 0) && (this.props.verificationComplete !== 1)) {
                
                let clickedOff = localStorage.getItem("activateSteps");

                if (!clickedOff && this.state.backdropOpen && this.props.accountStatus === 2 && this.props.personaStatus !== 'passed') {
                  //with a backdrop
                  return (
                    <Backdrop open={this.state.backdropOpen} className={classes.backdrop} onClick={() => this.handleClose() }>
                      <div className={classes.stepsdiv}>
                        {steps}
                      </div>
                    </Backdrop>
                  );  


                } else {

                  //no backdrop modal
                  return (
                    <div>
                      {steps}
                    </div>
                  );
                }
              } else {
                //account or its state does not get any steps - show nothing...
                return (null);
              }

  
            }
            
              return (<div><LoadingSpinner/></div>);
            
            }
        }
        />
      );




  }
}

export default withStyles(styleSheet)(AccountActivationSteps);